/**
 * Pure-function chip-state derivation for the per-tile "needs attention"
 * chip.
 *
 * Per `reference-rules-in-comments` (ai.knowledge/global). The chip
 * system contract:
 *
 *   - Reactive runtime + add-time
 *   - Terminal verdicts only (UNKNOWN / VALIDATING never trigger)
 *   - Diagnostic verbs ("Authenticate" / "Connect video" /
 *     "Connect controls")
 *   - Single warning severity
 *   - One chip per tile via priority cascade
 *
 * Per Extended Discovery invariant #9 (per-aspect AuthenticationStatus,
 * one driver writes per aspect): each aspect's authentication and
 * connection status is read independently. The aggregate "any aspect
 * INVALID → Authenticate" follows from the priority cascade, not from
 * a pre-aggregated `device.authenticationStatus` field.
 *
 * Per `think-in-systems` (ai.knowledge/global): pure-function extraction
 * lets the call sites test chip rendering without rendering the full
 * status / proxy / livekit graph. Tests are pure-function unit tests
 * (no ESM mocking required — see `esm-jest-mock-patterns.md`).
 *
 * Returns `null` (no chip) for:
 *   - bridge-offline (handled by existing `<Status>` BADGE_STATUS_BRIDGE_OFFLINE)
 *   - offline-embedded (Iris-enabled was originally OUT of bulk-add scope)
 *   - all-healthy WITH a resolved model
 *   - any aspect still in transient state (UNKNOWN / VALIDATING / CONNECTING)
 *   - a connection failure reported while the DEVICE-level status is still
 *     transient (connecting / setting up / booting) — see
 *     `isConnectionVerdictDue`
 *   - no bridge status yet (avoids the chip flashing during the initial
 *     device-add transient before the bridge reports anything)
 *
 * Returns `Select model` (Phase 1 manual QA addition) when the device
 * is otherwise healthy but the discovery never resolved a `modelId` —
 * the tile would otherwise look fine while quietly running with the
 * "Other" generic driver, hiding the fact that PTZ + the right URX
 * are not configured. The original Phase 1 plan deferred this trigger
 * to P2 (carpet-bomb risk in the risks table), but manual QA surfaced
 * the missing signal on NDI sources that connect successfully without
 * a fingerprint match — re-included here gated on a `status`-exists
 * check so the chip never appears during the add-transient window.
 */
import {
  AuthenticationStatus,
  StatusDeviceConnectionStatus
} from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { StatusDevice } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { Agent } from '@studio/models/agent';
import { DefaultModelId } from '@studio/models/device';
import type { Model } from '@studio/models/device';
import type { T_Maybe } from '@tryiris-ai/js-types';
// Transitory-placeholder discriminator (W2, spec §2). A synthetic
// placeholder device carries `transitoryKind`; `deriveChipState` reads
// it to surface the "Authenticate" chip (spec §3.3) WITHOUT a real
// agent status — a placeholder has no proxy.
import type { SetupAttention } from '#~/device/setup-attention.js';
import type { TransitoryKind } from '#~/device/transitory.js';
// The full missing-set enumerator (`deriveMissingBuckets`, ENG-4486 §2.1)
// returns canonical `MultiAddSkipReason` keys so the notification grouping
// can decide single-vs-combined. Type-only import — no runtime coupling
// (notifications/types.js pulls in only design-system + js-types types).
// Per `reference-rules-in-comments` (ai.knowledge/global).
import type { MultiAddSkipReason } from '#~/notifications/types.js';
// Shared setup order (auth -> video -> driver -> control), also read by the
// multi-add notification router. Dependency-free module.
import { firstInSetupOrder, sortBySetupOrder } from '#~/device/setup-order.js';
import type { SETUP_BUCKET_ORDER } from '#~/device/setup-order.js';

/** Tab the chip's action link deep-links into in Advanced Settings. */
export type ChipTargetTab = 'authentication' | 'video' | 'control' | 'driver';

/** Derived chip state — non-null when the tile has something to show. */
export interface ChipState {
  /** Diagnostic verb (e.g. "Authenticate", "Connect video", "Connect controls"). */
  actionLink: string;

  /** Tab to focus when the chip's link is clicked. */
  targetTab: ChipTargetTab;
}

/** A bucket the chip cascade can surface (the members of `SETUP_BUCKET_ORDER`). */
type CascadeBucket = (typeof SETUP_BUCKET_ORDER)[number];

/** Chip verb + deep-link tab for each cascade bucket. */
const CHIP_FOR_BUCKET: Readonly<Record<CascadeBucket, ChipState>> = {
  'auth': { actionLink: 'Authenticate', targetTab: 'authentication' },
  'video-failed': { actionLink: 'Connect video', targetTab: 'video' },
  'missing-model': { actionLink: 'Select model', targetTab: 'driver' },
  'control-failed': { actionLink: 'Connect controls', targetTab: 'control' }
};

// --- Shared aspect predicates (ENG-4486 §2.1) --------------------------------
// Extracted so `deriveChipState` (the single top-priority verdict) and
// `deriveMissingBuckets` (the full missing-set enumerator) read the SAME
// truth for each aspect — a copy-paste of the boolean expressions would let
// the chip verb and the grouped notification drift apart. Each helper is the
// exact expression `deriveChipState` used inline before the extraction, so
// the cascade's output stays byte-identical (proven by the untouched
// `chip-state.test.ts`).
// Per `think-in-systems` (ai.knowledge/global): one predicate, two callers.
// Per `reference-rules-in-comments` (ai.knowledge/global).

/**
 * True when EITHER aspect reports terminal AUTHENTICATION_STATUS_INVALID.
 * Per Extended Discovery invariant #9, video + movement carry independent
 * auth verdicts; either INVALID surfaces the "Authenticate" verb.
 */
const isAuthInvalid = (status: T_Maybe<StatusDevice>): boolean =>
  status?.video?.authenticationStatus === AuthenticationStatus.AUTHENTICATION_STATUS_INVALID ||
  status?.movement?.authenticationStatus === AuthenticationStatus.AUTHENTICATION_STATUS_INVALID;

/**
 * Device-level statuses during which a per-aspect CONNECTION_STATUS_FAILED is
 * NOT a terminal verdict: the device is starting up, transitioning, or
 * intentionally off, so "could not connect" has not been decided yet.
 *
 * Numeric literals rather than `StatusDeviceOverallStatus.*`: a STATIC named
 * import of that enum cannot be linked by jest's `--experimental-vm-modules`
 * ESM linker — the same documented limitation that already forced the interop
 * stubs in `jest.config.js` (radix, `ws`, `cnfast`, the `@studio/models`
 * drivers) — and this module is loaded directly by the pure-function unit
 * tests. Proto enum numbers are append-only wire contract per
 * `wire-contract-changes-must-not-break-old-firmware` (ai.knowledge/global),
 * so they are stable; `chip-state.overall-gate.test.ts` pins every one of them
 * against the real enum so a renumber can never pass silently.
 *
 * CONNECTION_FAILED / CONNECTED / READY are deliberately absent — those are
 * settled, and a failed aspect there is real. Keeping CONNECTED/READY out of
 * this set is load-bearing for the CONTROL verb: a camera whose video is fine
 * but whose control transport failed sits at overall CONNECTED (evalStatus
 * ignores control unless there is no video/audio), and that chip must still
 * fire.
 */
const TRANSIENT_OVERALL_STATUSES: ReadonlySet<number> = new Set([
  0, // OVERALL_STATUS_UNKNOWN
  1, // OVERALL_STATUS_CONNECTING
  5, // OVERALL_STATUS_REBOOTING
  6, // OVERALL_STATUS_SLEEPING
  7, // OVERALL_STATUS_WAKING
  8, // OVERALL_STATUS_SHUTTING_DOWN
  9 // OVERALL_STATUS_SETTING_UP
]);

/**
 * Whether a connection verdict is due for this device yet.
 *
 * A device that is still connecting can momentarily publish a per-aspect
 * CONNECTION_STATUS_FAILED — a single failed RTSP handshake retry is enough —
 * while its own device-level status still says CONNECTING. Reading the aspect
 * field alone therefore accuses a camera that is merely starting up, which is
 * what users saw as a "Connect video" chip and a "Configure Video"
 * notification flashing for a second or two before the video appeared.
 *
 * The encoder no longer emits that contradiction, but this gate is NOT
 * redundant: per `wire-contract-changes-must-not-break-old-firmware`
 * (ai.knowledge/global), Studio auto-updates while customer bridge firmware
 * does not, so an un-updated encoder keeps sending the transient FAILED
 * indefinitely. This is the half of the fix that protects those users.
 *
 * It also adopts the precedence the rest of the client already uses: the legacy
 * `<Status>` badge resolves `overallStatus` FIRST and returns, which is exactly
 * why this contradiction stayed invisible for years until a surface read the
 * aspect field without consulting overall.
 *
 * Absent overall status gates PERMISSIVELY (no verdict), per
 * `absent-data-gates-permissively` (ai.knowledge/global) — an unknown state
 * must never be converted into an accusation.
 *
 * A HELD bucket bypasses this gate on purpose: the hold only ever contains a
 * verdict that already passed the gate once, and its whole job is to survive
 * the CONNECTING half of the encoder's INTENTIONAL retry cycle (that cycle
 * animates the connection badge in the centre of the preview, so it must not
 * be flattened at the source). The offline / placeholder branches still win,
 * because they sit ABOVE the connection branches in the cascade — so a hold
 * can never mask a bridge-offline tile. See `connection-failure-hold.ts`.
 */
const isConnectionVerdictDue = (status: T_Maybe<StatusDevice>): boolean => {
  const overall = status?.overallStatus?.status;
  if (overall === undefined) return false;
  return !TRANSIENT_OVERALL_STATUSES.has(overall);
};

/**
 * Whether a HELD verdict may be honoured for this snapshot.
 *
 * The hold exists to bridge ONE thing: the CONNECTING half of the encoder's
 * intentional retry cycle, during which the gate would otherwise blink the
 * verdict off. It must NOT be honoured for every transient state the gate
 * covers, because most of them cannot release it:
 *
 *   - SLEEPING / SHUTTING_DOWN / REBOOTING / WAKING: `setPowerState` never
 *     reconciles the aspects, so the aspect stays FAILED and the release
 *     condition can never arrive. A held verdict there is a permanent, false,
 *     clickable accusation on a camera that is merely off — and "intentionally
 *     off" is named in `isConnectionVerdictDue` as exactly a state where
 *     FAILED is not a verdict.
 *   - UNKNOWN / absent: nothing is known, so per
 *     `absent-data-gates-permissively` (ai.knowledge/global) it must gate
 *     permissively rather than resurrect an old accusation.
 *   - SETTING_UP: a settings commit is in flight; the device is about to be
 *     re-added and re-evaluated from scratch.
 *
 * CONNECTED / READY / CONNECTION_FAILED are settled, so the fresh predicate
 * decides and the hold is redundant there — honouring it is harmless and keeps
 * the verdict continuous across the CONNECTION_FAILED -> CONNECTING flip.
 */
const isHoldHonoured = (status: T_Maybe<StatusDevice>): boolean => {
  const overall = status?.overallStatus?.status;
  if (overall === undefined) return false;
  return !HOLD_DENIED_OVERALL_STATUSES.has(overall);
};

/**
 * Overall statuses that must NOT honour a held verdict — every transient state
 * the gate covers EXCEPT `CONNECTING`, plus `UNKNOWN`. See `isHoldHonoured`.
 */
const HOLD_DENIED_OVERALL_STATUSES: ReadonlySet<number> = new Set([
  0, // OVERALL_STATUS_UNKNOWN
  5, // OVERALL_STATUS_REBOOTING
  6, // OVERALL_STATUS_SLEEPING
  7, // OVERALL_STATUS_WAKING
  8, // OVERALL_STATUS_SHUTTING_DOWN
  9 // OVERALL_STATUS_SETTING_UP
]);

/**
 * True when the video transport hit terminal CONNECTION_STATUS_FAILED AND the
 * device has settled far enough for that to be a verdict rather than a
 * mid-connect blip. Both `deriveChipState` and `deriveMissingBuckets` read this
 * one predicate, so the chip verb and the grouped notification cannot disagree
 * about whether the failure is real.
 */
const isVideoConnectionFailed = (
  status: T_Maybe<StatusDevice>,
  held: readonly MultiAddSkipReason[] = []
): boolean =>
  (isHoldHonoured(status) && held.includes('video-failed')) ||
  (isConnectionVerdictDue(status) &&
    status?.video?.connectionStatus?.status ===
      StatusDeviceConnectionStatus.CONNECTION_STATUS_FAILED);

/** Control counterpart of `isVideoConnectionFailed`, same settled-state gate. */
const isControlConnectionFailed = (
  status: T_Maybe<StatusDevice>,
  held: readonly MultiAddSkipReason[] = []
): boolean =>
  (isHoldHonoured(status) && held.includes('control-failed')) ||
  (isConnectionVerdictDue(status) &&
    status?.movement?.connectionStatus?.status ===
      StatusDeviceConnectionStatus.CONNECTION_STATUS_FAILED);

/**
 * True when a status snapshot exists AND the model is unresolved (missing or
 * the generic `DefaultModelId`). Gated on `status` so the verdict never
 * flashes during the add-transient window before the bridge reports anything.
 */
const isModelMissing = (status: T_Maybe<StatusDevice>, modelId: T_Maybe<Model['id']>): boolean =>
  !!status && (!modelId || modelId === DefaultModelId);

/**
 * Derive the chip state for a single device tile.
 *
 * @param status - Raw `StatusDevice` protobuf snapshot from the agent
 *   state-sync tree (typically `proxy?.state.status?.device[deviceId]`).
 *   `undefined` means the bridge has not yet sent a status for this
 *   device — treated as transient (no chip).
 * @param device - Subset of the studio Device. `agentStatus` gates the
 *   bridge-offline / offline-embedded branches; `modelId` (Phase 1
 *   manual QA addition) drives the "Select model" branch surfaced
 *   when a device is otherwise healthy but its model never resolved.
 *   Pass `Pick<Device, 'agentStatus' | 'modelId'>` to keep the call
 *   sites decoupled from the full Device shape.
 * @returns `null` when no chip should render; `ChipState` otherwise.
 */
export const deriveChipState = (
  status: T_Maybe<StatusDevice>,
  device: {
    agentStatus?: T_Maybe<Agent['status']>;
    modelId?: T_Maybe<Model['id']>;
    /**
     * Set ONLY on a synthetic transitory placeholder (W2 `toSyntheticDevice`,
     * spec §2/§4). Real devices never carry it. Present → the device is an
     * unauthenticated bulk-add placeholder and the chip is always
     * "Authenticate" (spec §3.3).
     */
    transitoryKind?: T_Maybe<TransitoryKind>;
    /**
     * True when the device belongs to an embedded (Iris-enabled) agent.
     * Embedded devices cannot have their video/control/model configured
     * from the studio — suppress "Connect video", "Connect controls", and
     * "Select model" chips that would deep-link to hidden tabs.
     */
    isEmbedded?: boolean;
    /**
     * Connection buckets HELD steady for this device across the encoder's
     * retry cycle (`connection-failure-hold.ts`). Empty for callers that do
     * not participate in the hold.
     */
    heldConnectionBuckets?: readonly MultiAddSkipReason[];
    /** Reactive setup evidence shared with the sidebar config badge. */
    setupAttention?: T_Maybe<SetupAttention>;
  }
): ChipState | null => {
  // Priority 0 — transitory placeholder (spec §3.3). An unauthenticated
  // bulk-add placeholder has no real agent/proxy status to read, so the
  // verdict is unconditional: it must be authenticated before it can do
  // anything. Checked FIRST so the synthesized status (or its absence)
  // never short-circuits this case. The chip deep-links to the
  // Authentication tab in Advanced Settings, where the auth dispatcher
  // (W4) replays the probe/link by `transitoryKind`.
  if (device.transitoryKind) {
    return { actionLink: 'Authenticate', targetTab: 'authentication' };
  }

  // Priority 1 — bridge offline. NOT a chip case: existing <Status>
  // already shows BADGE_STATUS_BRIDGE_OFFLINE for this state. Returning
  // null here lets <Status> remain the surface (the suppression rule
  // in item.tsx only applies when chipState !== null).
  if (device.agentStatus === 'offline') return null;

  // Iris-enabled offline-embedded: deferred (originally out of bulk-
  // add scope; Iris-enabled rows have disabled checkboxes). Return
  // null defensively.
  if (device.agentStatus === 'offline-embedded') return null;

  // Priority 2 — Auth INVALID on EITHER aspect. Per Extended Discovery
  // invariant #9, video and movement (control) carry independent
  // authenticationStatus; either INVALID surfaces "Authenticate".
  if (device.setupAttention === 'authentication' || isAuthInvalid(status)) {
    return { actionLink: 'Authenticate', targetTab: 'authentication' };
  }

  // Embedded (Iris-enabled) devices: video, control, and model are managed
  // by the camera's own embedded agent — the studio cannot configure them.
  // Suppress chips that deep-link to tabs hidden for embedded devices.
  // Only "Authenticate" (priority 2 above) is valid for these devices.
  if (device.isEmbedded) return null;

  // Priorities 3-5 — video / driver / control. Which one wins is decided by
  // the shared `SETUP_BUCKET_ORDER` (device/setup-order.ts), the same order
  // the multi-add notification router uses, so the chip and the toast never
  // send the user to different tabs. Driver precedes Control: the driver
  // picks the control protocol, so a generic camera must not be sent to
  // Control (ONVIF) before a driver is chosen (QA finding 8).
  // Per `think-in-systems` (ai.knowledge/global).
  //
  // Video CONNECTION_FAILED — terminal state only; CONNECTING / UNKNOWN
  // intentionally fall through. Per engineer override 2026-05-14: verb is
  // "Connect video" (was "Reconnect") so the user can tell at a glance
  // WHICH aspect needs attention.
  //
  // Movement (control) CONNECTION_FAILED — same terminal-only rule. Per
  // engineer override 2026-05-14: verb is "Connect controls" (was
  // "Reconnect") to disambiguate from the video case.
  //
  // Model unidentified — Phase 1 manual QA addition:
  // when the bulk-add (or any commit path) lands a device whose
  // `modelId` either is missing or resolves to `DefaultModelId` (the
  // "Other" generic placeholder defined by @studio/models/device
  // constants.ts — `f4694b47…dcbb77e2`), the tile silently runs with
  // the generic driver. Video may connect but PTZ + URX use defaults
  // instead of the registry-template values. Surface "Select model"
  // so the user can pick a driver from the Driver tab and unlock
  // model-specific behavior.
  //
  // The bridge always assigns SOME modelId on commit — if no
  // fingerprint match, it falls back to `DefaultModelId` rather than
  // leaving the field unset, so the missing-id check alone is not
  // enough. Compare against the constant directly.
  //
  // Raw-status callers wait for a snapshot. The UI can also supply explicit
  // setup evidence (default model or a loaded generic template), which needs
  // no transport verdict before offering the Driver tab.
  const pending: CascadeBucket[] = [];
  if (isVideoConnectionFailed(status, device.heldConnectionBuckets)) pending.push('video-failed');
  if (isControlConnectionFailed(status, device.heldConnectionBuckets)) {
    pending.push('control-failed');
  }
  if (device.setupAttention === 'driver' || isModelMissing(status, device.modelId)) {
    pending.push('missing-model');
  }
  const next = firstInSetupOrder(pending);

  // Healthy with a resolved model / transient / unrecognized — no chip.
  return next ? CHIP_FOR_BUCKET[next] : null;
};

/**
 * Enumerate ALL factually-missing configuration buckets for a device
 * (ENG-4486 §2.1) — the full-set counterpart to `deriveChipState`, which
 * returns only the single top-priority verdict.
 *
 * Why a separate enumerator: the "two alerts for one camera" field report
 * (spec §2A) is TEMPORAL ACCUMULATION — the cascade surfaces one issue per
 * tick and each fired alert is sticky-deduped, so two issues pile up as the
 * device's top verdict shifts. Grouping into ONE combined alert per device
 * (PRD §9.2) needs the whole set at once, not the single cascade pick.
 *
 * Buckets are returned in the shared `SETUP_BUCKET_ORDER` (**auth,
 * video-failed, missing-model, control-failed** — driver before control, QA
 * finding 8), using the SAME shared aspect predicates `deriveChipState`
 * reads, so the two functions can never disagree about what is wrong or what
 * to fix first. The caller decides single-vs-combined from `.length`.
 *
 * Suppression mirrors the cascade exactly:
 *   - transitory placeholder → `['auth']` (priority 0).
 *   - bridge offline / offline-embedded → `[]` (nothing actionable).
 *   - embedded (Iris-enabled) → auth-only: video / control / model are
 *     managed on-device, so a COMBINED alert is UNREACHABLE for an embedded
 *     device (spec §6.6). This is a documented invariant, not dead code.
 *
 * @param status - Raw `StatusDevice` snapshot (same input as `deriveChipState`).
 * @param device - Same `Pick<Device, …>` shape `deriveChipState` accepts.
 * @returns Ordered list of missing buckets; empty when nothing is wrong.
 */
export const deriveMissingBuckets = (
  status: T_Maybe<StatusDevice>,
  device: {
    agentStatus?: T_Maybe<Agent['status']>;
    modelId?: T_Maybe<Model['id']>;
    transitoryKind?: T_Maybe<TransitoryKind>;
    isEmbedded?: boolean;
    heldConnectionBuckets?: readonly MultiAddSkipReason[];
  }
): MultiAddSkipReason[] => {
  // Priority 0 — transitory placeholder is unconditionally auth-only
  // (mirrors `deriveChipState`'s first branch; no proxy status to read).
  if (device.transitoryKind) return ['auth'];

  // Bridge offline / offline-embedded: <Status> owns these states and there
  // is nothing the chip deep-link could fix — no missing buckets.
  if (device.agentStatus === 'offline') return [];
  if (device.agentStatus === 'offline-embedded') return [];

  const missing: MultiAddSkipReason[] = [];

  // Auth first — matches the cascade's priority 2 and keeps auth at the head
  // of the ordered set so the combined router can prefer it (§6.2).
  if (isAuthInvalid(status)) missing.push('auth');

  // Embedded (Iris-enabled): video / control / model are managed by the
  // camera's own agent — the studio cannot configure them (cascade returns
  // null past this point). Stop here → at most auth is ever missing, so a
  // combined alert is unreachable for embedded devices (spec §6.6).
  if (device.isEmbedded) return missing;

  if (isVideoConnectionFailed(status, device.heldConnectionBuckets)) {
    missing.push('video-failed');
  }
  if (isControlConnectionFailed(status, device.heldConnectionBuckets)) {
    missing.push('control-failed');
  }
  if (isModelMissing(status, device.modelId)) missing.push('missing-model');

  // Order by the shared setup order rather than push order, so the head of
  // the list is always the tab the chip and the notification route to.
  return sortBySetupOrder(missing);
};
